import React from "react";

interface SuggestionsMenuProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;

  /** The input field the menu is attached to */
  children: React.ReactNode;

  /** The content of the menu */
  renderMenu: () => React.ReactNode;
}

const minHeight = 200;

export const SuggestionsMenu = (props: SuggestionsMenuProps) => {
  type Display = "above" | "below";

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [display, setDisplay] = React.useState<Display>("below");

  React.useLayoutEffect(() => {
    if (!props.isOpen) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - wrapperRect.bottom;
    const spaceAbove = wrapperRect.top;

    setDisplay(spaceBelow < minHeight && spaceAbove > spaceBelow ? "above" : "below");
  }, [props.isOpen]);

  React.useEffect(() => {
    if (!props.isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current.contains(event.target as Node)) {
        props.onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [props.isOpen, props.onClose]);

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (props.isOpen && e.key === "Escape") {
      e.stopPropagation();
      props.onClose();
    }
  }

  return <div ref={wrapperRef} style={{ position: "relative" }} onKeyDown={onKeyDown}>
    <div onFocus={props.onOpen} onClick={props.onOpen}>
      {props.children}
    </div>

    {props.isOpen && <div
      className="suggestions-menu"
      style={display === "below" ? { top: "100%", marginTop: 8 } : { bottom: "100%", marginBottom: 8 }}
    >
      {props.renderMenu()}
    </div>}
  </div>;
}

interface SuggestionsMenuOptionProps {
  onClick: () => void;
  children: React.ReactNode;
}

export const SuggestionsMenuOption = (props: SuggestionsMenuOptionProps) => {
  return <div className="suggestions-menu-option" onClick={props.onClick}>
    {props.children}
  </div>;
}
